"use client";

import { compressImage } from "@/lib/compressImage";
import {
  ANALYTICS_EVENTS,
  track,
} from "@/lib/analyticsEvents";
import {
  type ComplexityLevel,
  type ComprehensionExerciseType,
  computeConfigTotal,
  type FunctionsExerciseType,
  GLOBAL_TEST_DEFAULTS,
  type GrammarExerciseType,
  type LanguageLevel,
  type LessonAnalysis,
  QUIZ_DEFAULTS,
  type TestConfig,
  type TestType,
  testTargetTotal,
  type VocabularyExerciseType,
} from "@/lib/testTypes";
import { cn } from "@/lib/utils";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useMutation } from "convex/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  FileImage,
  ImagePlus,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import Image from "next/image";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

const LESSON_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const MAX_LESSON_IMAGES = 8;
const SAMPLE_IMAGE_PATH = "/images/landing/textbook-william-kamkwamba.png";

export type TestCreationStep = "lesson_content" | "test_setup" | "generate";
export type TestSource = "upload" | "sample" | "existing_lesson";

export type TestGenerationContext = {
  source: TestSource;
  fileCount: number;
  grade: string;
};

type LocalImage = {
  key: string;
  preview: string;
  file: File;
  storageId?: Id<"_storage">;
  uploading: boolean;
  error?: string;
};

const STEPS: Array<{ id: TestCreationStep; label: string }> = [
  { id: "lesson_content", label: "Lesson content" },
  { id: "test_setup", label: "Test setup" },
  { id: "generate", label: "Generate" },
];

const GRADE_OPTIONS = [
  "1st Year Middle School",
  "2nd Year Middle School",
  "3rd Year Middle School",
  "Common Core",
  "1st Year Bac",
  "2nd Year Bac",
];

const COMPREHENSION_EXERCISE_OPTIONS: Array<{
  value: ComprehensionExerciseType;
  label: string;
}> = [
  { value: "mcq", label: "Multiple choice" },
  { value: "true_false", label: "True / false + correction" },
  { value: "answer_questions", label: "Answer questions" },
  { value: "referents", label: "Referents" },
  { value: "complete_sentences", label: "Complete sentences" },
];

const GRAMMAR_EXERCISE_OPTIONS: Array<{
  value: GrammarExerciseType;
  label: string;
}> = [
  { value: "verb_form", label: "Correct tense or form" },
  { value: "adverb_position", label: "Adverb position" },
  { value: "mcq", label: "Multiple choice" },
  { value: "write_questions", label: "Write questions" },
  { value: "complete_sentences", label: "Complete sentences" },
];

const VOCABULARY_EXERCISE_OPTIONS: Array<{
  value: VocabularyExerciseType;
  label: string;
}> = [
  { value: "fill_gap", label: "Fill in the gap" },
  { value: "odd_word", label: "Odd word out" },
  { value: "match_definitions", label: "Match definitions" },
  { value: "complete_sentences", label: "Complete sentences" },
];

const FUNCTIONS_EXERCISE_OPTIONS: Array<{
  value: FunctionsExerciseType;
  label: string;
}> = [
  { value: "situation_response", label: "Situation response" },
  { value: "write_question", label: "Write the question" },
  { value: "complete_dialogue", label: "Complete a dialogue" },
];

const LANGUAGE_LEVEL_OPTIONS: Array<{ value: LanguageLevel; label: string }> = [
  { value: "A1", label: "A1 · Beginner" },
  { value: "A2", label: "A2 · Elementary" },
  { value: "B1", label: "B1 · Intermediate" },
  { value: "B2", label: "B2 · Upper-intermediate" },
];

const COMPLEXITY_OPTIONS: Array<{ value: ComplexityLevel; label: string }> = [
  { value: "easy", label: "Accessible" },
  { value: "medium", label: "Balanced" },
  { value: "hard", label: "Challenging" },
];

function durationMinutes(testType: TestType): number {
  return testType === "quiz" ? 30 : 60;
}

function includedSections(config: TestConfig): string[] {
  return [
    config.comprehension.included ? "Comprehension" : null,
    config.language.included ? "Language" : null,
    config.writing.included ? "Writing" : null,
  ].filter((value): value is string => value !== null);
}

export function TestForm({
  onAnalyse,
  onGenerate,
  onSaveProfile,
  analysing,
  generating,
  hasGenerated,
  analysis,
  canUseTests,
  teacherName,
  schoolName,
  isFirstTest,
  planTier,
  onStepChange,
  onMeaningfulProgress,
  resumeDraft,
}: {
  onAnalyse: (payload: {
    lessonImageIds: Id<"_storage">[];
    grade: string;
    testType: TestType;
  }) => Promise<boolean>;
  onGenerate: (
    config: TestConfig,
    context: TestGenerationContext,
  ) => Promise<void>;
  onSaveProfile: (teacherName: string, schoolName: string) => Promise<boolean>;
  analysing: boolean;
  generating: boolean;
  hasGenerated: boolean;
  analysis: LessonAnalysis | null;
  canUseTests: boolean;
  teacherName?: string;
  schoolName?: string;
  isFirstTest: boolean;
  planTier: string;
  onStepChange: (step: TestCreationStep, source: TestSource | null) => void;
  onMeaningfulProgress: (progress: {
    source: TestSource | null;
    step: TestCreationStep;
  }) => void;
  resumeDraft?: { grade: string; testType: TestType };
}) {
  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const deleteUploadedFiles = useMutation(api.files.deleteUploadedFiles);
  const uploadInputId = useId();
  const viewedSteps = useRef(new Set<TestCreationStep>());
  const completedSteps = useRef(new Set<TestCreationStep>());
  const setupCompleted = useRef(false);
  const [stepIndex, setStepIndex] = useState(resumeDraft ? 1 : 0);
  const [source, setSource] = useState<TestSource | null>(resumeDraft ? "existing_lesson" : null);
  const [images, setImages] = useState<LocalImage[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [grade, setGrade] = useState(resumeDraft?.grade ?? GRADE_OPTIONS[0]);
  const [config, setConfig] = useState<TestConfig>(resumeDraft?.testType === "global_test" ? GLOBAL_TEST_DEFAULTS : QUIZ_DEFAULTS);
  const [teacher, setTeacher] = useState(teacherName ?? "");
  const [school, setSchool] = useState(schoolName ?? "");
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const [preparedSignature, setPreparedSignature] = useState<string | null>(resumeDraft ? "resume" : null);

  const step = STEPS[stepIndex]?.id ?? "lesson_content";
  const uploadedImages = images.filter(
    (image): image is LocalImage & { storageId: Id<"_storage"> } =>
      Boolean(image.storageId && !image.uploading && !image.error),
  );
  const allUploaded = Boolean(resumeDraft && analysis) ||
    images.length > 0 &&
    uploadedImages.length === images.length &&
    !sampleBusy;
  const target = testTargetTotal(config.testType);
  const total = computeConfigTotal(config);
  const totalValid = total === target;
  const questionTypesValid =
    (!config.comprehension.included || config.comprehension.exercises.length > 0) &&
    (!config.language.included ||
      ((!config.language.grammar.included || config.language.grammar.exercises.length > 0) &&
        (!config.language.vocabulary.included || config.language.vocabulary.exercises.length > 0) &&
        (!config.language.functions.included || config.language.functions.exercises.length > 0)));
  const sections = includedSections(config);
  const profileValid = Boolean(teacher.trim() && school.trim());

  useEffect(() => {
    onStepChange(step, source);
    onMeaningfulProgress({ source, step });
    if (viewedSteps.current.has(step)) return;
    viewedSteps.current.add(step);
    track(ANALYTICS_EVENTS.testStepViewed, {
      step,
      source,
      is_first_test: isFirstTest,
    });
  }, [isFirstTest, onMeaningfulProgress, onStepChange, source, step]);

  const markSource = useCallback(
    (nextSource: TestSource) => {
      if (source === nextSource) return;
      setSource(nextSource);
      track(ANALYTICS_EVENTS.testSourceSelected, {
        source: nextSource,
        is_first_test: isFirstTest,
      });
    },
    [isFirstTest, source],
  );

  const trackStepCompleted = useCallback(
    (completedStep: TestCreationStep, completedSource: TestSource) => {
      if (completedSteps.current.has(completedStep)) return;
      completedSteps.current.add(completedStep);
      track(ANALYTICS_EVENTS.testStepCompleted, {
        step: completedStep,
        source: completedSource,
        is_first_test: isFirstTest,
      });
    },
    [isFirstTest],
  );

  const uploadOne = useCallback(
    async (item: LocalImage): Promise<boolean> => {
      setImages((current) =>
        current.map((image) =>
          image.key === item.key
            ? { ...image, uploading: true, error: undefined }
            : image,
        ),
      );
      try {
        const uploadUrl = await generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": item.file.type },
          body: item.file,
        });
        if (!response.ok) throw new Error("Upload failed");
        const payload = (await response.json()) as {
          storageId: Id<"_storage">;
        };
        setImages((current) =>
          current.map((image) =>
            image.key === item.key
              ? {
                  ...image,
                  storageId: payload.storageId,
                  uploading: false,
                  error: undefined,
                }
              : image,
          ),
        );
        return true;
      } catch {
        setImages((current) =>
          current.map((image) =>
            image.key === item.key
              ? {
                  ...image,
                  uploading: false,
                  error: "Could not upload. Try again.",
                }
              : image,
          ),
        );
        return false;
      }
    },
    [generateUploadUrl],
  );

  const addFiles = useCallback(
    async (
      files: File[],
      selectedSource: "upload" | "sample",
      currentImageCount = images.length,
    ) => {
      setUploadError(null);
      const imageFiles = files.filter((file) => file.type.startsWith("image/"));
      if (imageFiles.length !== files.length) {
        setUploadError("Use JPG, PNG, WEBP, or another supported image file.");
      }
      if (imageFiles.length === 0) return;
      const availableSlots = MAX_LESSON_IMAGES - currentImageCount;
      if (availableSlots <= 0) {
        setUploadError(`You can add up to ${MAX_LESSON_IMAGES} lesson pages.`);
        return;
      }
      const selected = imageFiles.slice(0, availableSlots);
      if (imageFiles.length > availableSlots) {
        setUploadError(
          `Only ${availableSlots} more page${availableSlots === 1 ? "" : "s"} can be added. The limit is ${MAX_LESSON_IMAGES}.`,
        );
      }

      const prepared: File[] = [];
      for (const file of selected) {
        try {
          const next = await compressImage(file, LESSON_IMAGE_MAX_BYTES);
          if (next.size > LESSON_IMAGE_MAX_BYTES) {
            setUploadError("Each lesson page must be 3 MB or smaller.");
            continue;
          }
          prepared.push(next);
        } catch {
          setUploadError("One image could not be prepared. Try a clearer JPG or PNG.");
        }
      }

      const created = prepared.map((file, index): LocalImage => ({
        key: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
        preview: URL.createObjectURL(file),
        file,
        uploading: true,
      }));
      if (created.length === 0) return;
      setImages((current) => [...current, ...created]);
      setPreparedSignature(null);
      const uploadResults = await Promise.all(created.map(uploadOne));

      if (selectedSource === "upload") {
        const successfulFiles = prepared.filter((_, index) => uploadResults[index]);
        if (successfulFiles.length > 0) {
          track(ANALYTICS_EVENTS.testFileUploaded, {
            source: "upload",
            file_count: successfulFiles.length,
            file_types: Array.from(new Set(successfulFiles.map((file) => file.type))).join(","),
            is_first_test: isFirstTest,
          });
        }
      }
    },
    [images.length, isFirstTest, uploadOne],
  );

  const handleUserFiles = useCallback(
    async (files: FileList | File[]) => {
      markSource("upload");
      await addFiles(Array.from(files), "upload");
    },
    [addFiles, markSource],
  );

  const chooseUploadSource = useCallback(() => {
    if (source === "sample") {
      const sampleStorageIds = images
        .map((image) => image.storageId)
        .filter((storageId): storageId is Id<"_storage"> => Boolean(storageId));
      if (sampleStorageIds.length > 0) {
        void deleteUploadedFiles({ lessonImageIds: sampleStorageIds }).catch(() => {});
      }
      images.forEach((image) => URL.revokeObjectURL(image.preview));
      setImages([]);
      setPreparedSignature(null);
    }
    markSource("upload");
  }, [deleteUploadedFiles, images, markSource, source]);

  const loadSample = useCallback(async () => {
    markSource("sample");
    setSampleBusy(true);
    setUploadError(null);
    const previousStorageIds = images
      .map((image) => image.storageId)
      .filter((storageId): storageId is Id<"_storage"> => Boolean(storageId));
    if (previousStorageIds.length > 0) {
      void deleteUploadedFiles({ lessonImageIds: previousStorageIds }).catch(() => {});
    }
    images.forEach((image) => URL.revokeObjectURL(image.preview));
    setImages([]);
    try {
      const response = await fetch(SAMPLE_IMAGE_PATH);
      if (!response.ok) throw new Error("Sample unavailable");
      const blob = await response.blob();
      const file = new File([blob], "darsi-sample-lesson.png", {
        type: blob.type || "image/png",
      });
      await addFiles([file], "sample", 0);
    } catch {
      setUploadError("The sample lesson could not be prepared. Try again.");
    } finally {
      setSampleBusy(false);
    }
  }, [addFiles, deleteUploadedFiles, images, markSource]);

  const removeImage = useCallback((key: string) => {
    setImages((current) => {
      const targetImage = current.find((image) => image.key === key);
      if (targetImage) {
        URL.revokeObjectURL(targetImage.preview);
        if (targetImage.storageId) {
          void deleteUploadedFiles({ lessonImageIds: [targetImage.storageId] }).catch(() => {});
        }
      }
      return current.filter((image) => image.key !== key);
    });
    setPreparedSignature(null);
  }, [deleteUploadedFiles]);

  const selectTestType = useCallback((testType: TestType) => {
    setConfig(testType === "quiz" ? QUIZ_DEFAULTS : GLOBAL_TEST_DEFAULTS);
    setPreparedSignature(null);
    track(ANALYTICS_EVENTS.testTypeSelected, { test_type: testType });
  }, []);

  const goToSetup = useCallback(() => {
    if (!source || !allUploaded) return;
    trackStepCompleted("lesson_content", source);
    setStepIndex(1);
  }, [allUploaded, source, trackStepCompleted]);

  const prepareReview = useCallback(async () => {
    if (!source || !allUploaded || !profileValid || !totalValid || !questionTypesValid) return;
    setProfileError(null);
    const profileChanged =
      teacher.trim() !== (teacherName ?? "").trim() ||
      school.trim() !== (schoolName ?? "").trim();
    if (profileChanged) {
      setProfileBusy(true);
      const saved = await onSaveProfile(teacher.trim(), school.trim());
      setProfileBusy(false);
      if (!saved) {
        setProfileError("We could not save your test header details. Try again.");
        return;
      }
    }

    const ids = uploadedImages.map((image) => image.storageId);
    const signature = `${source}:${grade}:${config.testType}:${ids.join(",")}`;
    if (preparedSignature !== signature) {
      const prepared = await onAnalyse({
        lessonImageIds: ids,
        grade,
        testType: config.testType,
      });
      if (!prepared) return;
      setPreparedSignature(signature);
    }

    const properties = {
      source,
      test_type: config.testType,
      grade_level: grade,
      difficulty: config.complexity,
      duration_minutes: durationMinutes(config.testType),
      section_count: sections.length,
      file_count: ids.length,
      is_first_test: isFirstTest,
    } as const;
    if (!setupCompleted.current) {
      setupCompleted.current = true;
      track(ANALYTICS_EVENTS.testSetupCompleted, properties);
    }
    trackStepCompleted("test_setup", source);
    setStepIndex(2);
  }, [
    allUploaded,
    config.complexity,
    config.testType,
    grade,
    isFirstTest,
    onAnalyse,
    onSaveProfile,
    preparedSignature,
    profileValid,
    questionTypesValid,
    school,
    schoolName,
    sections.length,
    source,
    teacher,
    teacherName,
    trackStepCompleted,
    totalValid,
    uploadedImages,
  ]);

  const submitGeneration = useCallback(async () => {
    if (!source || generating || hasGenerated) return;
    trackStepCompleted("generate", source);
    await onGenerate(config, {
      source,
      fileCount: uploadedImages.length,
      grade,
    });
  }, [config, generating, grade, hasGenerated, onGenerate, source, trackStepCompleted, uploadedImages.length]);

  const inputClass =
    "min-h-11 w-full rounded-xl border border-input bg-card px-3 text-sm text-foreground outline-none transition-[border-color,box-shadow] focus:border-success focus:ring-2 focus:ring-success/20";

  return (
    <div className="flex flex-col gap-6">
      <CreationProgress currentStep={stepIndex} onSelect={(index) => {
        if (index < stepIndex) setStepIndex(index);
      }} />

      {step === "lesson_content" ? (
        <section aria-labelledby="lesson-content-heading" className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">
              Step 1 of 3
            </p>
            <h2 id="lesson-content-heading" className="mt-1 text-xl font-semibold text-foreground">
              Add the lesson you want to assess
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              Upload clear textbook pages or lesson screenshots.
            </p>
          </div>

          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="sr-only">Choose a lesson source</legend>
            <SourceButton
              active={source === "upload"}
              icon={<ImagePlus className="h-5 w-5" />}
              title="Upload lesson pages"
              description="Use your own textbook pages"
              onClick={chooseUploadSource}
            />
            <SourceButton
              active={source === "sample"}
              icon={<Sparkles className="h-5 w-5" />}
              title="Try a sample lesson"
              description="Explore with a Moroccan EFL sample"
              onClick={() => void loadSample()}
              disabled={sampleBusy}
            />
          </fieldset>

          {source === "upload" ? (
            <label
              htmlFor={uploadInputId}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                void handleUserFiles(event.dataTransfer.files);
              }}
              className={cn(
                "flex min-h-40 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-5 py-7 text-center outline-none transition-[border-color,background-color,box-shadow] focus-within:ring-2 focus-within:ring-success/30",
                dragActive
                  ? "border-success bg-success/10"
                  : "border-input bg-muted/25 hover:border-success/60 hover:bg-muted/50",
              )}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-success/10 text-success">
                <Upload className="h-5 w-5" />
              </span>
              <span className="mt-3 text-sm font-semibold text-foreground">
                Drop pages here or click to select
              </span>
              <span className="mt-1 text-xs text-muted-foreground">
                JPG, PNG or WEBP · 3 MB per page · up to {MAX_LESSON_IMAGES} pages
              </span>
              <input
                id={uploadInputId}
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                onChange={(event) => {
                  if (event.target.files) void handleUserFiles(event.target.files);
                  event.target.value = "";
                }}
              />
            </label>
          ) : null}

          {source === "sample" ? (
            <div className="flex items-center gap-3 rounded-2xl border border-success/25 bg-success/5 p-4">
              <div className="relative h-16 w-14 shrink-0 overflow-hidden rounded-lg border border-border bg-card">
                <Image
                  src={SAMPLE_IMAGE_PATH}
                  alt="Sample English textbook lesson page"
                  fill
                  className="object-cover"
                  sizes="56px"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Sample lesson · Education and ambition</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  A controlled textbook page about William Kamkwamba, prepared through the real test pipeline.
                </p>
              </div>
              {sampleBusy ? <Loader2 className="h-5 w-5 animate-spin text-success" /> : null}
            </div>
          ) : null}

          {images.length > 0 ? (
            <div className="space-y-2" aria-live="polite">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">
                  {images.length} lesson page{images.length === 1 ? "" : "s"}
                </p>
                {source === "upload" && images.length < MAX_LESSON_IMAGES ? (
                  <label htmlFor={uploadInputId} className="cursor-pointer text-xs font-semibold text-success hover:underline">
                    Add more
                  </label>
                ) : null}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {images.map((image, index) => (
                  <div key={image.key} className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card p-2.5">
                    <div className="relative h-14 w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
                      <Image src={image.preview} alt={`Lesson page ${index + 1}`} fill className="object-cover" sizes="48px" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-foreground">Page {index + 1}</p>
                      <p className={cn("mt-0.5 text-[11px]", image.error ? "text-destructive" : "text-muted-foreground")}>
                        {image.uploading ? "Uploading…" : image.error ?? "Ready"}
                      </p>
                    </div>
                    {image.error ? (
                      <button type="button" onClick={() => void uploadOne(image)} className="flex h-9 w-9 items-center justify-center rounded-lg text-success hover:bg-success/10" aria-label={`Retry page ${index + 1}`}>
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    ) : null}
                    {source === "upload" ? (
                      <button type="button" onClick={() => removeImage(image.key)} className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={`Remove page ${index + 1}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {uploadError ? <p role="alert" className="text-sm text-destructive">{uploadError}</p> : null}

          <button
            type="button"
            disabled={!allUploaded}
            onClick={goToSetup}
            aria-describedby={!allUploaded ? "content-requirement" : undefined}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-success px-4 text-sm font-semibold text-success-foreground transition hover:bg-success/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2"
          >
            Continue to test setup
            <ArrowRight className="h-4 w-4" />
          </button>
          {!allUploaded ? (
            <p id="content-requirement" className="text-center text-xs text-muted-foreground">
              {images.some((image) => image.uploading)
                ? "Wait for every lesson page to finish uploading."
                : "Upload at least one lesson page to continue."}
            </p>
          ) : null}
        </section>
      ) : null}

      {step === "test_setup" ? (
        <section aria-labelledby="test-setup-heading" className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">Step 2 of 3</p>
            <h2 id="test-setup-heading" className="mt-1 text-xl font-semibold text-foreground">Shape your assessment</h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Start with the essentials. Darsi has already balanced the sections and marks.</p>
          </div>

          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-foreground">Grade level</span>
            <select value={grade} onChange={(event) => { setGrade(event.target.value); setPreparedSignature(null); }} className={inputClass}>
              {GRADE_OPTIONS.map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Assessment type</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <TestTypeCard type="quiz" active={config.testType === "quiz"} onClick={() => selectTestType("quiz")} />
              <TestTypeCard type="global_test" active={config.testType === "global_test"} onClick={() => selectTestType("global_test")} />
            </div>
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-sm font-medium text-foreground">Difficulty</span>
              <select value={config.complexity} onChange={(event) => setConfig((current) => ({ ...current, complexity: event.target.value as ComplexityLevel }))} className={inputClass}>
                {COMPLEXITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-foreground">Duration</span>
              <div className="flex min-h-11 items-center rounded-xl border border-border bg-muted/30 px-3 text-sm text-foreground">
                {durationMinutes(config.testType)} minutes · matched to {config.testType === "quiz" ? "quiz" : "global test"}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-muted/20 p-4">
            <div className="flex items-start gap-3">
              <FileImage className="mt-0.5 h-5 w-5 shrink-0 text-success" />
              <div>
                <p className="text-sm font-semibold text-foreground">Printable answer key included</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">Every generated test includes a separate teacher answer key and classroom-ready student copy.</p>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-sm font-medium text-foreground">Teacher name</span>
              <input value={teacher} onChange={(event) => setTeacher(event.target.value)} className={inputClass} aria-invalid={!teacher.trim()} />
            </label>
            <label className="space-y-1.5">
              <span className="text-sm font-medium text-foreground">School</span>
              <input value={school} onChange={(event) => setSchool(event.target.value)} className={inputClass} aria-invalid={!school.trim()} />
            </label>
          </div>

          <div className="overflow-hidden rounded-2xl border border-border">
            <button type="button" onClick={() => setMoreOptionsOpen((open) => !open)} aria-expanded={moreOptionsOpen} className="flex min-h-12 w-full items-center justify-between gap-3 bg-card px-4 text-left text-sm font-semibold text-foreground hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-success">
              More options
              <ChevronDown className={cn("h-4 w-4 transition-transform", moreOptionsOpen && "rotate-180")} />
            </button>
            {moreOptionsOpen ? (
              <div className="space-y-4 border-t border-border bg-muted/15 p-4">
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium text-muted-foreground">English level (CEFR)</span>
                  <select value={config.languageLevel} onChange={(event) => setConfig((current) => ({ ...current, languageLevel: event.target.value as LanguageLevel }))} className={inputClass}>
                    {LANGUAGE_LEVEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <SectionEditor title="I. Comprehension" included={config.comprehension.included} required={config.testType === "global_test"} points={config.comprehension.points} onToggle={(included) => setConfig((current) => ({ ...current, comprehension: { ...current.comprehension, included } }))} onPoints={(points) => setConfig((current) => ({ ...current, comprehension: { ...current.comprehension, points } }))}>
                  <CheckboxList options={COMPREHENSION_EXERCISE_OPTIONS} selected={config.comprehension.exercises} onChange={(exercises) => setConfig((current) => ({ ...current, comprehension: { ...current.comprehension, exercises } }))} />
                </SectionEditor>
                <SectionEditor title="II. Language" included={config.language.included} required={config.testType === "global_test"} points={config.language.points} onToggle={(included) => setConfig((current) => ({ ...current, language: { ...current.language, included } }))} onPoints={(points) => setConfig((current) => {
                  const oldTotal = current.language.points;
                  const ratio = oldTotal > 0 ? points / oldTotal : 1;
                  const grammarPoints = Math.round(current.language.grammar.points * ratio * 2) / 2;
                  const vocabularyPoints = Math.round(current.language.vocabulary.points * ratio * 2) / 2;
                  const functionsPoints = Math.max(0, Math.round((points - grammarPoints - vocabularyPoints) * 2) / 2);
                  return {
                    ...current,
                    language: {
                      ...current.language,
                      points,
                      grammar: { ...current.language.grammar, points: grammarPoints },
                      vocabulary: { ...current.language.vocabulary, points: vocabularyPoints },
                      functions: { ...current.language.functions, points: functionsPoints },
                    },
                  };
                })}>
                  <p className="text-xs font-medium text-muted-foreground">Grammar</p>
                  <CheckboxList options={GRAMMAR_EXERCISE_OPTIONS} selected={config.language.grammar.exercises} onChange={(exercises) => setConfig((current) => ({ ...current, language: { ...current.language, grammar: { ...current.language.grammar, exercises } } }))} />
                  <p className="pt-2 text-xs font-medium text-muted-foreground">Vocabulary</p>
                  <CheckboxList options={VOCABULARY_EXERCISE_OPTIONS} selected={config.language.vocabulary.exercises} onChange={(exercises) => setConfig((current) => ({ ...current, language: { ...current.language, vocabulary: { ...current.language.vocabulary, exercises } } }))} />
                  <p className="pt-2 text-xs font-medium text-muted-foreground">Functions</p>
                  <CheckboxList options={FUNCTIONS_EXERCISE_OPTIONS} selected={config.language.functions.exercises} onChange={(exercises) => setConfig((current) => ({ ...current, language: { ...current.language, functions: { ...current.language.functions, exercises } } }))} />
                </SectionEditor>
                <SectionEditor title="III. Writing" included={config.writing.included} required={config.testType === "global_test"} points={config.writing.points} onToggle={(included) => setConfig((current) => ({ ...current, writing: { ...current.writing, included } }))} onPoints={(points) => setConfig((current) => ({ ...current, writing: { ...current.writing, points } }))}>
                  <label className="flex min-h-10 cursor-pointer items-center gap-2 text-xs text-foreground">
                    <input type="checkbox" checked={config.writing.guided} onChange={(event) => setConfig((current) => ({ ...current, writing: { ...current.writing, guided: event.target.checked } }))} className="h-4 w-4 accent-green-700" />
                    Include guided writing support
                  </label>
                </SectionEditor>
                <div className={cn("flex items-center justify-between rounded-xl border px-3 py-2.5", totalValid ? "border-success/30 bg-success/5" : "border-destructive/30 bg-destructive/5")}>
                  <span className="text-sm font-semibold text-foreground">Point distribution</span>
                  <span className={cn("text-sm font-bold", totalValid ? "text-success" : "text-destructive")}>{total} / {target}</span>
                </div>
              </div>
            ) : null}
          </div>

          {profileError ? <p role="alert" className="text-sm text-destructive">{profileError}</p> : null}
          {!profileValid ? <p className="text-xs text-muted-foreground">Add the teacher and school names used in the printable test header.</p> : null}
          {!totalValid ? <p role="alert" className="text-xs text-destructive">Point distribution must total {target}. It currently totals {total}.</p> : null}
          {!questionTypesValid ? <p role="alert" className="text-xs text-destructive">Choose at least one question type for every included section.</p> : null}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button type="button" onClick={() => setStepIndex(0)} className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button type="button" disabled={!profileValid || !totalValid || !questionTypesValid || analysing || profileBusy} onClick={() => void prepareReview()} className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-success px-5 text-sm font-semibold text-success-foreground hover:bg-success/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
              {analysing ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparing questions from your lesson…</> : profileBusy ? "Saving test header…" : <>Review my test <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        </section>
      ) : null}

      {step === "generate" ? (
        <section aria-labelledby="generate-heading" className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-success">Step 3 of 3</p>
            <h2 id="generate-heading" className="mt-1 text-xl font-semibold text-foreground">Review and generate</h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Everything is ready for a polished classroom assessment.</p>
          </div>

          {analysis ? (
            <div className="rounded-2xl border border-success/25 bg-success/5 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-success">Lesson focus</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{analysis.lessonTopic}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{analysis.componentNotes}</p>
            </div>
          ) : null}

          <dl className="divide-y divide-border rounded-2xl border border-border bg-card px-4">
            <SummaryRow label="Content source" value={source === "existing_lesson" ? "Saved draft analysis" : source === "sample" ? "Darsi sample lesson" : `${uploadedImages.length} uploaded lesson page${uploadedImages.length === 1 ? "" : "s"}`} />
            <SummaryRow label="Grade" value={grade} />
            <SummaryRow label="Assessment" value={`${config.testType === "quiz" ? "Quiz" : "Global test"} · /${target}`} />
            <SummaryRow label="Duration" value={`${durationMinutes(config.testType)} minutes`} />
            <SummaryRow label="Difficulty" value={COMPLEXITY_OPTIONS.find((option) => option.value === config.complexity)?.label ?? config.complexity} />
            <SummaryRow label="Sections" value={sections.join(" · ")} />
            <SummaryRow label="Answer key" value="Included" />
          </dl>

          <div className="rounded-xl border border-success/25 bg-success/5 px-4 py-3">
            <p className="text-sm font-semibold text-foreground">Included in your current plan</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{planTier === "trial" ? "Your active trial includes Tests." : "Your Pro plan includes Tests with no per-test credit deduction."}</p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button type="button" onClick={() => setStepIndex(1)} className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success">
              <ArrowLeft className="h-4 w-4" /> Change setup
            </button>
            <button type="button" disabled={generating || hasGenerated || !canUseTests} onClick={() => void submitGeneration()} className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-success px-6 text-sm font-semibold text-success-foreground shadow-sm hover:bg-success/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2">
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Building your test…</> : hasGenerated ? <><Check className="h-4 w-4" /> Test ready</> : <><Sparkles className="h-4 w-4" /> Generate my test</>}
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function CreationProgress({ currentStep, onSelect }: { currentStep: number; onSelect: (index: number) => void }) {
  return (
    <nav aria-label="Test creation progress">
      <ol className="grid grid-cols-3 gap-2">
        {STEPS.map((item, index) => {
          const complete = index < currentStep;
          const active = index === currentStep;
          return (
            <li key={item.id}>
              <button type="button" disabled={index > currentStep} onClick={() => onSelect(index)} aria-current={active ? "step" : undefined} className={cn("flex min-h-11 w-full items-center gap-2 rounded-xl border px-2.5 text-left text-xs font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-success", active ? "border-success bg-success/10 text-foreground" : complete ? "border-border bg-card text-foreground hover:border-success/40" : "cursor-default border-border bg-muted/20 text-muted-foreground")}>
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px]", active || complete ? "bg-success text-success-foreground" : "border border-border bg-card")}>
                  {complete ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className="hidden leading-tight sm:block">{item.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function SourceButton({ active, icon, title, description, onClick, disabled = false }: { active: boolean; icon: React.ReactNode; title: string; description: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active} className={cn("relative flex min-h-24 items-start gap-3 rounded-2xl border p-4 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2", active ? "border-success bg-success/8 shadow-sm" : "border-border bg-card hover:border-success/40 hover:bg-muted/20")}>
      <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", active ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground")}>{icon}</span>
      <span>
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground">{title}{active ? <Check className="h-4 w-4 text-success" aria-label="Selected" /> : null}</span>
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

function TestTypeCard({ type, active, onClick }: { type: TestType; active: boolean; onClick: () => void }) {
  const quiz = type === "quiz";
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn("relative min-h-28 rounded-2xl border p-4 text-left outline-none transition focus-visible:ring-2 focus-visible:ring-success focus-visible:ring-offset-2", active ? "border-success bg-success/8 shadow-sm" : "border-border bg-card hover:border-success/40")}>
      <span className="flex items-start justify-between gap-3">
        <span>
          <span className="block text-base font-semibold text-foreground">{quiz ? "Quiz · /10" : "Global test · /20"}</span>
          <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{quiz ? "Short assessment · 20–30 minutes" : "Complete assessment · 45–60 minutes"}</span>
        </span>
        {active ? <span className="flex h-6 w-6 items-center justify-center rounded-full bg-success text-success-foreground"><Check className="h-3.5 w-3.5" /></span> : null}
      </span>
      {active ? <span className="mt-3 inline-block text-[11px] font-semibold uppercase tracking-[0.08em] text-success">Selected</span> : null}
    </button>
  );
}

function SectionEditor({ title, included, required = false, points, onToggle, onPoints, children }: { title: string; included: boolean; required?: boolean; points: number; onToggle: (included: boolean) => void; onPoints: (points: number) => void; children: React.ReactNode }) {
  return (
    <fieldset className={cn("rounded-xl border p-3", included ? "border-border bg-card" : "border-border/60 bg-muted/20")}>
      <legend className="sr-only">{title}</legend>
      <div className="flex items-center justify-between gap-3">
        <label className="flex min-h-10 cursor-pointer items-center gap-2 text-sm font-semibold text-foreground">
          <input type="checkbox" checked={included} disabled={required} onChange={(event) => onToggle(event.target.checked)} className="h-4 w-4 accent-green-700 disabled:opacity-60" />
          {title}
          {required ? <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Required</span> : null}
        </label>
        {included ? <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><input type="number" min={0} max={20} step={0.5} value={points} onChange={(event) => { const next = Number(event.target.value); if (Number.isFinite(next)) onPoints(next); }} className="h-9 w-16 rounded-lg border border-input bg-card px-2 text-right text-sm font-semibold text-foreground outline-none focus:ring-2 focus:ring-success/20" /> pts</label> : null}
      </div>
      {included ? <div className="mt-2 border-t border-border pt-2">{children}</div> : null}
    </fieldset>
  );
}

function CheckboxList<T extends string>({ options, selected, onChange }: { options: Array<{ value: T; label: string }>; selected: T[]; onChange: (selected: T[]) => void }) {
  return (
    <div className="grid gap-1 sm:grid-cols-2">
      {options.map((option) => (
        <label key={option.value} className="flex min-h-9 cursor-pointer items-center gap-2 rounded-lg px-2 text-xs text-foreground hover:bg-muted/40">
          <input type="checkbox" checked={selected.includes(option.value)} onChange={(event) => onChange(event.target.checked ? [...selected, option.value] : selected.filter((value) => value !== option.value))} className="h-4 w-4 accent-green-700" />
          {option.label}
        </label>
      ))}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}
